/**
 * The "Travel" dev-panel group (plan R17, §2.7): the cruise law, drops, mass-lock radii, interdiction, lanes and relay
 * transit, written into the live `reach-travel.json` config the runtime, the session and the encounter port share,
 * plus the runtime's large-ship mass. UI-rate code.
 */
import type { TuningGroup, TuningParam } from '../../render/contracts'
import type { TravelConfigs } from './state'

type Spec = readonly [key: string, get: (c: TravelConfigs) => number, set: (c: TravelConfigs, v: number) => void, min: number, max: number, step: number]

const SPECS: readonly Spec[] = [
  ['cruiseK', (c) => c.travel.cruise.k, (c, v) => (c.travel.cruise.k = v), 0.01, 2, 0.01],
  ['cruiseVMax', (c) => c.travel.cruise.vMax, (c, v) => (c.travel.cruise.vMax = v), 1e6, 1e11, 1e6],
  ['cruiseLagS', (c) => c.travel.cruise.lagS, (c, v) => (c.travel.cruise.lagS = v), 0, 10, 0.1],
  ['cruiseSurfaceFactor', (c) => c.travel.cruise.surfaceFactor, (c, v) => (c.travel.cruise.surfaceFactor = v), 1, 50, 0.5],
  ['cruiseChargeS', (c) => c.travel.cruise.chargeS, (c, v) => (c.travel.cruise.chargeS = v), 0.1, 10, 0.1],
  ['cruiseRateScale', (c) => c.travel.cruise.rateScale, (c, v) => (c.travel.cruise.rateScale = v), 0.05, 1, 0.05],
  ['cruiseDropDistanceM', (c) => c.travel.cruise.dropDistanceM, (c, v) => (c.travel.cruise.dropDistanceM = v), 1000, 100_000, 500],
  ['cruiseThrottleRate', (c) => c.travel.cruise.throttleRate, (c, v) => (c.travel.cruise.throttleRate = v), 0.05, 5, 0.05],
  ['massLockStationM', (c) => c.travel.massLock.stationM, (c, v) => (c.travel.massLock.stationM = v), 500, 50_000, 100],
  ['massLockRelayM', (c) => c.travel.massLock.relayM, (c, v) => (c.travel.massLock.relayM = v), 200, 20_000, 100],
  ['massLockShipM', (c) => c.travel.massLock.shipM, (c, v) => (c.travel.massLock.shipM = v), 200, 20_000, 100],
  ['largeShipMassKg', (c) => c.runtime.largeShipMassKg, (c, v) => (c.runtime.largeShipMassKg = v), 1000, 1e7, 1000],
  ['interdictRollEveryS', (c) => c.travel.interdiction.rollEveryS, (c, v) => (c.travel.interdiction.rollEveryS = v), 1, 120, 1],
  ['interdictGraceS', (c) => c.travel.interdiction.graceAfterArrivalS, (c, v) => (c.travel.interdiction.graceAfterArrivalS = v), 0, 600, 5],
  ['interdictCooldownS', (c) => c.travel.interdiction.cooldownS, (c, v) => (c.travel.interdiction.cooldownS = v), 0, 900, 5],
  ['interdictEscapeS', (c) => c.travel.interdiction.escapeS, (c, v) => (c.travel.interdiction.escapeS = v), 0.5, 30, 0.5],
  ['interdictMaxS', (c) => c.travel.interdiction.maxS, (c, v) => (c.travel.interdiction.maxS = v), 1, 60, 0.5],
  ['interdictConeDeg', (c) => c.travel.interdiction.coneDeg, (c, v) => (c.travel.interdiction.coneDeg = v), 1, 45, 0.5],
  ['laneKMult', (c) => c.travel.lanes.kMult, (c, v) => (c.travel.lanes.kMult = v), 1, 10, 0.1],
  ['laneEntryConeDeg', (c) => c.travel.lanes.entryConeDeg, (c, v) => (c.travel.lanes.entryConeDeg = v), 1, 90, 1],
  ['laneEntryMaxSpeed', (c) => c.travel.lanes.entryMaxSpeed, (c, v) => (c.travel.lanes.entryMaxSpeed = v), 10, 2000, 10],
  ['transitAlignDeg', (c) => c.travel.transit.alignDeg, (c, v) => (c.travel.transit.alignDeg = v), 1, 90, 1],
  ['transitMaxSpeed', (c) => c.travel.transit.maxSpeed, (c, v) => (c.travel.transit.maxSpeed = v), 10, 2000, 10],
  ['transitTunnelMinS', (c) => c.travel.transit.tunnelMinS, (c, v) => (c.travel.transit.tunnelMinS = v), 0, 10, 0.1],
]

/** The group over `configs` (read when the panel applies, so the runtime's live objects are what it edits). */
export function createTravelTuning(configs: () => TravelConfigs): TuningGroup {
  const initial = configs()
  const params: Record<string, TuningParam> = {}
  for (const [key, get, , min, max, step] of SPECS) params[key] = { value: get(initial), min, max, step }
  return {
    name: 'Travel',
    params,
    apply(): void {
      const c = configs()
      for (const [key, get, set, min, max] of SPECS) {
        const v = params[key]?.value
        const n = typeof v === 'number' && Number.isFinite(v) ? v : get(c)
        set(c, n < min ? min : n > max ? max : n)
      }
      // Keep the law's clamp ordered and the tunnel window valid (their schemas require it).
      if (c.travel.cruise.vMax < c.travel.cruise.vMin) c.travel.cruise.vMax = c.travel.cruise.vMin
      if (c.travel.transit.tunnelMinS > c.travel.transit.tunnelMaxS) c.travel.transit.tunnelMinS = c.travel.transit.tunnelMaxS
    },
  }
}
